// HTTP client: JSON in/out, bearer auth, single-flight token refresh and
// typed API errors. Tokens: access token in memory, refresh token persisted
// so a reload keeps the session (rotated on every refresh).

export const API_BASE: string = import.meta.env.VITE_API_BASE_URL ?? '';

export class ApiError extends Error {
  status: number;
  code: string;
  fields: Record<string, string>;
  details: Record<string, unknown>;
  requestId?: string;

  constructor(status: number, code: string, message: string, details?: Record<string, unknown>, requestId?: string) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details ?? {};
    this.fields = (this.details.fields as Record<string, string>) ?? {};
    this.requestId = requestId;
  }
}

const REFRESH_KEY = 'hms.refreshToken';
let accessToken: string | null = null;
let refreshInFlight: Promise<boolean> | null = null;
let onSession: ((payload: AuthPayload | null) => void) | null = null;

export interface AuthPayload {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  [key: string]: unknown;
}

export const tokens = {
  get access() {
    return accessToken;
  },
  get refresh() {
    try {
      return localStorage.getItem(REFRESH_KEY);
    } catch {
      return null;
    }
  },
  set(payload: AuthPayload | null) {
    accessToken = payload?.accessToken ?? null;
    try {
      if (payload) localStorage.setItem(REFRESH_KEY, payload.refreshToken);
      else localStorage.removeItem(REFRESH_KEY);
    } catch {
      /* storage unavailable (private mode) — session lasts for this tab only */
    }
  },
};

/** Registers the listener notified when tokens are refreshed or the session ends. */
export function onSessionChange(listener: (payload: AuthPayload | null) => void) {
  onSession = listener;
}

async function parse(resp: Response): Promise<unknown> {
  if (resp.status === 204) return null;
  const text = await resp.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function toError(status: number, body: unknown): ApiError {
  const err = (body as { error?: { code?: string; message?: string; details?: Record<string, unknown>; requestId?: string } })?.error;
  if (err?.code) return new ApiError(status, err.code, err.message ?? err.code, err.details, err.requestId);
  if (status === 429) return new ApiError(status, 'TOO_MANY_REQUESTS', 'Too many requests');
  return new ApiError(status, status >= 500 ? 'INTERNAL_ERROR' : 'UNKNOWN', `HTTP ${status}`);
}

export async function refreshSession(): Promise<boolean> {
  const refresh = tokens.refresh;
  if (!refresh) return false;
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const resp = await fetch(`${API_BASE}/api/v1/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: refresh }),
        });
        const body = await parse(resp);
        if (!resp.ok) {
          // Only a definitive auth failure ends the session; network blips do not.
          if (resp.status === 401 || resp.status === 403) {
            tokens.set(null);
            onSession?.(null);
          }
          return false;
        }
        tokens.set(body as AuthPayload);
        onSession?.(body as AuthPayload);
        return true;
      } catch {
        return false;
      } finally {
        setTimeout(() => (refreshInFlight = null), 0);
      }
    })();
  }
  return refreshInFlight;
}

export interface RequestOptions {
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  headers?: Record<string, string>;
  auth?: boolean;
}

export async function request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
  const url = new URL(`${API_BASE}${path}`, window.location.origin);
  for (const [k, v] of Object.entries(opts.query ?? {})) {
    if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v));
  }
  const doFetch = () => {
    const headers: Record<string, string> = { Accept: 'application/json', ...(opts.headers ?? {}) };
    if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
    if (opts.auth !== false && accessToken) headers.Authorization = `Bearer ${accessToken}`;
    return fetch(url.toString(), {
      method,
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    });
  };

  let resp: Response;
  try {
    resp = await doFetch();
    if (resp.status === 401 && opts.auth !== false && (await refreshSession())) resp = await doFetch();
  } catch {
    throw new ApiError(0, 'NETWORK', 'Network error');
  }
  const body = await parse(resp);
  if (!resp.ok) throw toError(resp.status, body);
  return body as T;
}

/** Binary upload/download (patient photos) with the same auth and refresh. */
async function binary(method: 'GET' | 'PUT' | 'POST', path: string, blob?: Blob): Promise<Response> {
  const doFetch = () =>
    fetch(`${API_BASE}${path}`, {
      method,
      headers: { ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}), ...(blob ? { 'Content-Type': blob.type } : {}) },
      body: blob,
    });
  let resp: Response;
  try {
    resp = await doFetch();
    if (resp.status === 401 && (await refreshSession())) resp = await doFetch();
  } catch {
    throw new ApiError(0, 'NETWORK', 'Network error');
  }
  if (!resp.ok && !(method === 'GET' && resp.status === 404)) throw toError(resp.status, await parse(resp));
  return resp;
}

export async function uploadBlob<T>(path: string, blob: Blob): Promise<T> {
  const resp = await binary('PUT', path, blob);
  return (await parse(resp)) as T;
}

/** Adds an image to a gallery (item pictures, X-ray images). */
export async function postBlob<T>(path: string, blob: Blob): Promise<T> {
  const resp = await binary('POST', path, blob);
  return (await parse(resp)) as T;
}

/** Object URL for an authenticated image, or null when it does not exist. */
export async function fetchImageUrl(path: string): Promise<string | null> {
  const resp = await binary('GET', path);
  if (resp.status === 404) return null;
  return URL.createObjectURL(await resp.blob());
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query']) => request<T>('GET', path, { query }),
  post: <T>(path: string, body?: unknown, headers?: Record<string, string>) =>
    request<T>('POST', path, { body: body ?? {}, headers }),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, { body: body ?? {} }),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, { body: body ?? {} }),
  delete: <T>(path: string) => request<T>('DELETE', path),
  publicGet: <T>(path: string) => request<T>('GET', path, { auth: false }),
};

export interface Paged<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}
